import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Theme } from '@mui/material/styles';
import { Box, SxProps } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import Portal from './Portal';

interface FramePortalProps {
  sx?: SxProps<Theme>;
  className?: string;
  stylesheets?: string[];
  theme: Theme;
  children?: React.ReactNode;
  iframeID?: string;
  dir: string;
}

const FramePortal: React.FC<FramePortalProps> = ({
  children,
  theme,
  sx,
  className,
  stylesheets,
  iframeID,
  dir,
}) => {
  const iframeKey = React.useMemo(() => uuidv4(), []);
  const [iframe, setIframe] = React.useState<HTMLIFrameElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    setIframe(iframeRef.current);
  }, [iframeRef.current]);

  return (
    <>
      <Box
        component="iframe"
        sx={{
          width: '100%',
          height: '100%',
          border: 'none',
          paddingTop: 0,
          marginTop: 0,
          ...sx,
        }}
        className={className}
        title="main"
        id={iframeID}
        translate="no"
        dir={dir}
        ref={iframeRef}
      />
      {iframe?.contentDocument?.body && createPortal(
        <Portal
          document={iframe?.contentDocument}
          theme={theme}
        >
          {children}
        </Portal>,
        iframe?.contentDocument?.body,
        `${iframeKey}-body`,
      )}
      {iframe?.contentDocument?.head && createPortal(
        stylesheets?.map((s) => (
          <link key={s} href={s} rel="stylesheet" type="text/css" />
        )),
        iframe?.contentDocument?.head,
        `${iframeKey}-head`,
      )}
    </>
  );
};

export default FramePortal;
